import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Check, Star, ArrowRight, Sparkles, ShoppingCart } from 'lucide-react';
import { servicePricing } from '../mockData';
import { useCart } from '../context/CartContext';

const TabbedPricingSection = () => {
  const [activeTab, setActiveTab] = useState('combo');
  const { addToCart } = useCart();

  const tabs = [
    { id: 'combo', name: 'Combo Packages', icon: Sparkles },
    { id: 'webDevelopment', name: 'Web Development', icon: ArrowRight },
    { id: 'uiuxDesign', name: 'UI/UX Design', icon: ArrowRight },
    { id: 'blockchain', name: 'Blockchain', icon: ArrowRight },
    { id: 'mobileApp', name: 'Mobile Apps', icon: ArrowRight },
    { id: 'digitalMarketing', name: 'Digital Marketing', icon: ArrowRight }
  ];

  const currentPlans = servicePricing[activeTab] || [];

  return (
    <section className="py-32 px-4 lg:px-8 relative overflow-hidden bg-white">
      {/* Background decorative elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-light-pink opacity-20 blur-3xl rounded-full" />
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-mid-purple opacity-20 blur-3xl rounded-full" />
      </div>

      <div className="max-w-8xl mx-auto relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center mb-12"
        >
          <motion.div
            animate={{ rotate: [0, 10, -10, 0] }}
            transition={{ duration: 4, repeat: Infinity }}
            className="inline-block mb-4"
          >
            <span className="text-6xl">💎</span>
          </motion.div>
          <h2 className="text-5xl md:text-6xl lg:text-7xl font-black mb-6">
            <span className="bg-gradient-to-r from-mid-purple via-mid-pink to-light-blue bg-clip-text text-transparent">
              Service Pricing
            </span>
          </h2>
          <p className="text-xl md:text-2xl text-dark-grey max-w-3xl mx-auto leading-relaxed">
            Choose the perfect package for your needs
          </p>
        </motion.div>

        {/* Tabs */}
        <div className="flex flex-wrap justify-center gap-3 mb-16">
          {tabs.map((tab, index) => {
            const Icon = tab.icon;
            return (
              <motion.button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: index * 0.05 }}
                whileHover={{ scale: 1.05, y: -2 }}
                whileTap={{ scale: 0.95 }}
                className={`group px-6 py-3 rounded-full font-mono text-sm uppercase tracking-wider font-bold transition-all duration-300 flex items-center gap-2 ${
                  activeTab === tab.id
                    ? 'bg-black text-white shadow-lg'
                    : 'bg-grey/30 text-black hover:bg-grey/50'
                }`}
              >
                <Icon size={16} className={activeTab === tab.id ? 'text-light-pink' : ''} />
                {tab.name}
              </motion.button>
            );
          })}
        </div>

        {/* Pricing Cards with Animation */}
        <AnimatePresence mode="wait">
          <motion.div
            key={activeTab}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.4 }}
            className="grid grid-cols-1 lg:grid-cols-3 gap-8 lg:gap-6"
          >
            {currentPlans.map((plan, index) => (
              <motion.div
                key={plan.id}
                initial={{ opacity: 0, y: 30, scale: 0.95 }}
                animate={{ opacity: 1, y: 0, scale: plan.popular ? 1.05 : 1 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                whileHover={{ y: -10, scale: plan.popular ? 1.08 : 1.03 }}
                className="relative group"
              >
                {/* Popular badge */}
                {plan.popular && (
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    className="absolute -top-4 left-1/2 -translate-x-1/2 z-20"
                  >
                    <div className="bg-gradient-to-r from-mid-purple to-mid-pink text-white px-6 py-2 rounded-full font-mono text-xs uppercase tracking-wider font-bold flex items-center gap-2 shadow-lg">
                      <Star size={16} fill="currentColor" />
                      Best Value
                    </div>
                  </motion.div>
                )}

                <div 
                  className={`relative h-full rounded-3xl p-8 lg:p-10 border-2 transition-all duration-300 ${
                    plan.popular 
                      ? 'border-mid-purple shadow-2xl' 
                      : 'border-grey hover:border-mid-purple shadow-lg hover:shadow-2xl'
                  }`}
                  style={{ backgroundColor: plan.color }}
                >
                  {/* Background pattern */}
                  <div className="absolute inset-0 opacity-5 rounded-3xl">
                    <div className="absolute inset-0" style={{
                      backgroundImage: 'radial-gradient(circle at 20% 50%, rgba(0,0,0,0.15) 1px, transparent 1px)',
                      backgroundSize: '20px 20px'
                    }} />
                  </div>

                  <div className="relative z-10">
                    {/* Plan name */}
                    <h3 className="text-3xl font-black mb-2">{plan.name}</h3>
                    
                    {/* Price */}
                    <div className="mb-4">
                      <div className="flex items-baseline gap-2">
                        <span className="text-5xl md:text-6xl font-black">{plan.price}</span>
                        <span className="text-lg text-black/60">{plan.period}</span>
                      </div>
                    </div>

                    {/* Description */}
                    <p className="text-base text-black/70 mb-8 leading-relaxed">
                      {plan.description}
                    </p>

                    {/* Features list */}
                    <ul className="space-y-4 mb-8">
                      {plan.features.map((feature, idx) => (
                        <motion.li
                          key={idx}
                          initial={{ opacity: 0, x: -20 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ duration: 0.4, delay: 0.5 + idx * 0.05 }}
                          className="flex items-start gap-3"
                        >
                          <div className="bg-black/10 rounded-full p-1 mt-1">
                            <Check size={16} className="text-black" />
                          </div>
                          <span className="text-sm text-black/80 leading-relaxed">{feature}</span>
                        </motion.li>
                      ))}
                    </ul>

                    {/* CTA Button */}
                    <motion.button
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={() => addToCart({
                        id: `${activeTab}-${plan.name}`,
                        type: 'Pricing Package',
                        name: plan.name,
                        price: plan.price,
                        description: `${tabs.find(t => t.id === activeTab)?.name} - ${plan.name} package`,
                        features: plan.features
                      })}
                      className={`w-full py-4 rounded-full font-mono text-sm uppercase tracking-wider font-bold transition-all duration-300 flex items-center justify-center gap-2 group ${
                        plan.popular
                          ? 'bg-black text-white hover:bg-mid-purple'
                          : 'bg-black/10 text-black hover:bg-black hover:text-white'
                      }`}
                    >
                      <ShoppingCart size={18} />
                      Add to Cart
                    </motion.button>
                  </div>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </AnimatePresence>

        {/* Additional info */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.5 }}
          className="text-center mt-16"
        >
          <p className="text-lg text-dark-grey mb-4">
            Need a custom solution? Let's discuss your specific requirements.
          </p>
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className="px-8 py-3 rounded-full border-2 border-black text-black hover:bg-black hover:text-white transition-all duration-300 font-mono text-sm uppercase tracking-wider font-bold"
          >
            Contact Sales
          </motion.button>
        </motion.div>
      </div>
    </section>
  );
};

export default TabbedPricingSection;
